import type { DogAgeRange } from "~/data/dogAgeRanges"

const API_BASE = "/api/v1"

export interface ArchetypeSection {
  do: string[]
  dont: string[]
}

export interface ArchetypeCta {
  headline: string
  text: string
  button_label: string
}

export interface ArchetypeDeepDive {
  title: string
  content: string
}

export interface AskDynamoQuestion {
  icon: "brain" | "heart" | "sparkles"
  text: string
}

export interface AskDynamoProfile {
  example_questions: AskDynamoQuestion[]
}

export interface ArchetypeProfile {
  name: string
  icon: string
  population_percent: number
  tags: string[]
  hook_primary: string
  hook_secondary: string
  watch_out: string
  when_this_do_this: string
  daily_life: ArchetypeSection
  training: ArchetypeSection
  out_and_about: ArchetypeSection
  cta_signup: ArchetypeCta
  cta_premium: ArchetypeCta
  ask_dynamo: AskDynamoProfile
  deep_dives: ArchetypeDeepDive[]
}

export interface AssessmentSessionResponse {
  session_token: string
  dog_name: string
  sex: "male" | "female"
  age_range: DogAgeRange
  started_at: string
  completed_at: string | null
  archetype_key: string | null
  archetype_label: string | null
  archetype_explanation: string | null
  archetype_why_fits: string | null
  archetype: ArchetypeProfile | null
}

export async function createAssessmentSession(
  dogName: string,
  sex: "male" | "female",
  ageRange: DogAgeRange,
): Promise<AssessmentSessionResponse> {
  const res = await fetch(`${API_BASE}/assessment_sessions`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    credentials: "include",
    body: JSON.stringify({ dog_name: dogName, sex, age_range: ageRange }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.errors?.join(", ") || res.statusText)
  }
  return res.json()
}

export async function getAssessmentSession(token: string): Promise<AssessmentSessionResponse> {
  const res = await fetch(`${API_BASE}/assessment_sessions/${encodeURIComponent(token)}`, {
    headers: { "Accept": "application/json" },
    credentials: "include",
  })
  if (!res.ok) throw new Error("Session not found")
  return res.json()
}

export async function updateAssessmentSession(
  token: string,
  payload: { answers?: Record<string, string>; completed?: boolean }
): Promise<AssessmentSessionResponse> {
  const res = await fetch(`${API_BASE}/assessment_sessions/${encodeURIComponent(token)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.errors?.join(", ") || res.statusText)
  }
  return res.json()
}

/** Development only: create a completed session with a given archetype (e.g. ?type=D). */
export async function createDevShortcutSession(
  type: string,
  dogName?: string,
  sex?: "male" | "female",
  ageRange?: DogAgeRange,
): Promise<AssessmentSessionResponse> {
  const res = await fetch(`${API_BASE}/assessment_sessions/dev_shortcut`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      type,
      dog_name: dogName ?? "Dev Dog",
      sex: sex ?? "male",
      age_range: ageRange ?? "adult",
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.errors?.join(", ") || res.statusText)
  }
  return res.json()
}
